<script>
import { useEsignStore } from '../../stores/esign'
import SignatureStatusBadge from './SignatureStatusBadge.vue'
import EcPagination from '../ui/EcPagination.vue'
import EcSpinner from '../ui/EcSpinner.vue'
import EcButton from '../ui/EcButton.vue'
import EcActionMenu from '../ui/EcActionMenu.vue'

const SIGNATURE_STATUSES = ['DRAFT', 'CREATED', 'UPDATED', 'SENT', 'COMPLETED', 'DECLINED', 'EXPIRED', 'DELETED']

export default {
  name: 'SignatureTable',
  components: {
    SignatureStatusBadge,
    EcPagination,
    EcSpinner,
    EcButton,
    EcActionMenu,
  },
  props: {
    signableType: {
      type: String,
      required: true,
    },
    signableId: {
      type: [String, Number],
      required: true,
    },
    refreshKey: {
      type: Number,
      default: 0,
    },
    clickableRows: {
      type: Boolean,
      default: false,
    },
    actionsMode: {
      type: String,
      default: 'inline',
    },
  },
  data() {
    const store = useEsignStore()
    return {
      store,
      search: '',
      statusFilter: '',
      dateFrom: '',
      dateTo: '',
      perPage: 10,
      currentPage: 1,
      sortColumn: 'created_at',
      sortDirection: 'desc',
      loading: false,
      debouncedSearch: '',
      searchTimeout: null,
      SIGNATURE_STATUSES,
    }
  },
  computed: {
    filteredSignatures() {
      let items = [...(this.store.signatures || [])]

      // Search filter (name + signers)
      if (this.debouncedSearch) {
        const q = this.debouncedSearch.toLowerCase()
        items = items.filter((s) => this.matchesSearch(s, q))
      }

      // Status filter
      if (this.statusFilter) {
        items = items.filter((s) => s.status === this.statusFilter)
      }

      // Date range filter
      if (this.dateFrom) {
        const from = new Date(this.dateFrom)
        from.setHours(0, 0, 0, 0)
        items = items.filter((s) => s.created_at && new Date(s.created_at) >= from)
      }
      if (this.dateTo) {
        const to = new Date(this.dateTo)
        to.setHours(23, 59, 59, 999)
        items = items.filter((s) => s.created_at && new Date(s.created_at) <= to)
      }

      // Sort
      const col = this.sortColumn
      const dir = this.sortDirection
      items.sort((a, b) => {
        const aVal = (a[col] || '')
        const bVal = (b[col] || '')
        const cmp = String(aVal).localeCompare(String(bVal))
        return dir === 'asc' ? cmp : -cmp
      })

      return items
    },
    totalItems() {
      return this.filteredSignatures.length
    },
    lastPage() {
      return Math.max(1, Math.ceil(this.totalItems / this.perPage))
    },
    paginatedSignatures() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredSignatures.slice(start, start + this.perPage)
    },
    t() {
      return this.$ecTranslate
    },
  },
  watch: {
    search(val) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.debouncedSearch = val
        this.currentPage = 1
      }, 300)
    },
    statusFilter() {
      this.currentPage = 1
    },
    perPage() {
      this.currentPage = 1
    },
    dateFrom() {
      this.currentPage = 1
    },
    dateTo() {
      this.currentPage = 1
    },
    refreshKey() {
      this.fetchData()
    },
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        await this.store.listSignatures(this.signableType, this.signableId)
      } finally {
        this.loading = false
      }
    },
    getSigners(signature) {
      if (signature.signers && signature.signers.length) return signature.signers
      if (!signature.roles || !signature.roles.length) return []
      return signature.roles.flatMap((r) => r.signers || [])
    },
    signerFirstName(signer) {
      return signer.first_name || signer.firstname || ''
    },
    signerLastName(signer) {
      return signer.last_name || signer.lastname || ''
    },
    matchesSearch(signature, query) {
      if (signature.name && signature.name.toLowerCase().includes(query)) return true
      if (signature.code && signature.code.toLowerCase().includes(query)) return true
      const signers = this.getSigners(signature)
      return signers.some(
        (s) =>
          (s.email && s.email.toLowerCase().includes(query)) ||
          this.signerFirstName(s).toLowerCase().includes(query) ||
          this.signerLastName(s).toLowerCase().includes(query),
      )
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
    },
    sortIcon(column) {
      if (this.sortColumn !== column) return 'bi-chevron-expand'
      return this.sortDirection === 'asc' ? 'bi-chevron-up' : 'bi-chevron-down'
    },
    canEdit(signature) {
      return ['DRAFT', 'CREATED'].includes(signature.status)
    },
    canSend(signature) {
      return ['DRAFT', 'CREATED', 'DECLINED', 'EXPIRED'].includes(signature.status)
    },
    canDelete(signature) {
      return ['DRAFT', 'CREATED'].includes(signature.status)
    },
    getActions(signature) {
      const t = this.$ecTranslate
      const actions = [
        { key: 'view', label: t('ELIXIO_CONNECT.VIEW'), icon: 'eye' },
      ]
      if (this.canEdit(signature)) {
        actions.push({ key: 'edit', label: t('ELIXIO_CONNECT.EDIT'), icon: 'pencil' })
      }
      if (this.canSend(signature)) {
        actions.push({ key: 'send', label: t('ELIXIO_CONNECT.SEND'), icon: 'send' })
      }
      if (this.canDelete(signature)) {
        actions.push({ key: 'delete', label: t('ELIXIO_CONNECT.DELETE'), icon: 'trash', variant: 'danger', separator: true })
      }
      return actions
    },
    onActionSelect(key, signature) {
      if (key === 'view') this.$emit('view', signature)
      else if (key === 'edit') this.$emit('edit', signature)
      else if (key === 'send') this.$emit('send', signature)
      else if (key === 'delete') this.$emit('delete', signature)
    },
    onRowClick(signature) {
      if (this.clickableRows) {
        this.$emit('view', signature)
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<template>
  <div class="ec-signature-table">
    <!-- Filters row -->
    <div class="ec-flex ec-gap-2 ec-mb-3 ec-flex-wrap ec-items-end">
      <div class="ec-form-group" style="min-width: 80px; margin-bottom: 0">
        <select v-model="perPage" class="ec-select__field">
          <option :value="10">10</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
        </select>
      </div>
      <div class="ec-form-group" style="min-width: 160px; margin-bottom: 0">
        <select v-model="statusFilter" class="ec-select__field">
          <option value="">{{ t('ELIXIO_CONNECT.ALL_STATUSES') }}</option>
          <option v-for="s in SIGNATURE_STATUSES" :key="s" :value="s">
            {{ t(`ELIXIO_CONNECT.${s}`) }}
          </option>
        </select>
      </div>
      <div class="ec-form-group" style="min-width: 140px; margin-bottom: 0">
        <label class="ec-form-label" style="margin-bottom: 0; font-size: 0.6875rem">{{ t('ELIXIO_CONNECT.DATE_FROM') }}</label>
        <input v-model="dateFrom" type="date" class="ec-input__field" />
      </div>
      <div class="ec-form-group" style="min-width: 140px; margin-bottom: 0">
        <label class="ec-form-label" style="margin-bottom: 0; font-size: 0.6875rem">{{ t('ELIXIO_CONNECT.DATE_TO') }}</label>
        <input v-model="dateTo" type="date" class="ec-input__field" />
      </div>
      <div class="ec-form-group" style="flex: 1; min-width: 180px; margin-bottom: 0">
        <div class="ec-input ec-input--has-icon">
          <i class="ec-input__icon bi bi-search" />
          <input
            v-model="search"
            type="text"
            class="ec-input__field"
            :placeholder="t('ELIXIO_CONNECT.SEARCH') + '...'"
          />
        </div>
      </div>
      <EcButton variant="outline-secondary" size="sm" icon="arrow-clockwise" :loading="loading" @click="fetchData" />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="ec-text-center" style="padding: 2rem 0">
      <EcSpinner />
    </div>

    <!-- Table -->
    <div v-else-if="paginatedSignatures.length" class="ec-table-responsive">
      <table class="ec-table ec-table-hover ec-table-sm">
        <thead>
          <tr>
            <th style="min-width: 120px">Code</th>
            <th class="ec-table__sortable" @click="toggleSort('name')">
              {{ t('ELIXIO_CONNECT.NAME') }} <i class="bi" :class="sortIcon('name')" />
            </th>
            <th class="ec-table__sortable" @click="toggleSort('status')">
              {{ t('ELIXIO_CONNECT.STATUS') }} <i class="bi" :class="sortIcon('status')" />
            </th>
            <th>{{ t('ELIXIO_CONNECT.SIGNERS') }}</th>
            <th class="ec-table__sortable" @click="toggleSort('created_at')">
              {{ t('ELIXIO_CONNECT.CREATED_AT') }} <i class="bi" :class="sortIcon('created_at')" />
            </th>
            <th style="text-align: right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="signature in paginatedSignatures"
            :key="signature.id"
            :class="{ 'ec-table__row--clickable': clickableRows }"
            @click="onRowClick(signature)"
          >
            <td>
              <code class="ec-text-muted" style="font-size: 0.75rem">{{ signature.code || '' }}</code>
            </td>
            <td>{{ signature.name }}</td>
            <td>
              <SignatureStatusBadge :status="signature.status" />
            </td>
            <td>
              <div class="ec-signature-table__signers">
                <span
                  v-for="(signer, idx) in getSigners(signature)"
                  :key="idx"
                  class="ec-signature-table__signer"
                >
                  {{ signerFirstName(signer) }} {{ signerLastName(signer) }}
                  <small v-if="signer.email" class="ec-text-muted">({{ signer.email }})</small>
                </span>
                <span v-if="!getSigners(signature).length" class="ec-text-muted">-</span>
              </div>
            </td>
            <td>{{ formatDate(signature.created_at) }}</td>
            <td style="text-align: right" @click.stop>
              <!-- Inline actions (default) -->
              <div v-if="actionsMode === 'inline'" class="ec-flex ec-gap-1" style="justify-content: flex-end">
                <EcButton variant="outline-secondary" size="sm" icon="eye" :title="t('ELIXIO_CONNECT.VIEW')" @click="$emit('view', signature)" />
                <EcButton v-if="canEdit(signature)" variant="outline-primary" size="sm" icon="pencil" :title="t('ELIXIO_CONNECT.EDIT')" @click="$emit('edit', signature)" />
                <EcButton v-if="canSend(signature)" variant="outline-primary" size="sm" icon="send" :title="t('ELIXIO_CONNECT.SEND')" @click="$emit('send', signature)" />
                <EcButton v-if="canDelete(signature)" variant="outline-danger" size="sm" icon="trash" :title="t('ELIXIO_CONNECT.DELETE')" @click="$emit('delete', signature)" />
              </div>

              <!-- Dropdown actions -->
              <div v-else style="display: flex; justify-content: flex-end">
                <EcActionMenu
                  :actions="getActions(signature)"
                  @select="onActionSelect($event, signature)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <div v-else class="ec-text-center ec-text-muted" style="padding: 2rem 0">
      {{ t('ELIXIO_CONNECT.NO_SIGNATURES') }}
    </div>

    <!-- Pagination -->
    <div v-if="!loading && totalItems > 0" class="ec-flex ec-justify-between ec-items-center ec-mt-3">
      <small class="ec-text-muted">
        {{ t('ELIXIO_CONNECT.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES', { from: (currentPage - 1) * perPage + 1, to: Math.min(currentPage * perPage, totalItems), of: totalItems }) }}
      </small>
      <EcPagination
        :current-page="currentPage"
        :last-page="lastPage"
        @update:current-page="currentPage = $event"
      />
    </div>
  </div>
</template>
